import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  GripVertical,
  ChevronDown,
  ChevronRight,
  Copy,
  Trash2,
} from 'lucide-react';
import { FunnelSection } from '../../types/section.types';
import { useFunnelStore } from '../../store/funnelStore';

interface SectionItemProps {
  section: FunnelSection;
  index: number;
}

/**
 * Section Item Component
 * Represents a single section in the section tree with drag/drop and controls
 */
const SectionItem: React.FC<SectionItemProps> = ({ section }) => {
  const {
    selectedSectionId,
    selectSection,
    deleteSection,
    duplicateSection,
    toggleSectionCollapse,
  } = useFunnelStore();

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: section.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const isSelected = selectedSectionId === section.id;
  const isCollapsed = section.isCollapsed;

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (confirm(`Delete "${section.name}"?`)) {
      deleteSection(section.id);
    }
  };

  const handleDuplicate = (e: React.MouseEvent) => {
    e.stopPropagation();
    duplicateSection(section.id);
  };

  const handleToggleCollapse = (e: React.MouseEvent) => {
    e.stopPropagation();
    toggleSectionCollapse(section.id);
  };

  const handleSelect = () => {
    selectSection(section.id);
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`
        mx-2 mb-2 rounded-md border transition-all
        ${isSelected ? 'border-primary-500 bg-primary-50' : 'border-gray-200 bg-white'}
        ${isDragging ? 'shadow-lg' : 'hover:border-gray-300'}
      `}
    >
      <div
        className="flex items-center gap-2 p-3 cursor-pointer"
        onClick={handleSelect}
      >
        {/* Drag Handle */}
        <div
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600"
        >
          <GripVertical size={16} />
        </div>

        {/* Collapse Toggle */}
        <button
          onClick={handleToggleCollapse}
          className="text-gray-400 hover:text-gray-600"
        >
          {isCollapsed ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
        </button>

        {/* Section Info */}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-gray-900 truncate">
            {section.name}
          </div>
          <div className="text-xs text-gray-500">
            {section.type} • {section.columns.length} column{section.columns.length !== 1 ? 's' : ''}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-1">
          <button
            onClick={handleDuplicate}
            className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
            title="Duplicate section"
          >
            <Copy size={14} />
          </button>
          <button
            onClick={handleDelete}
            className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
            title="Delete section"
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      {/* Section Details (when not collapsed) */}
      {!isCollapsed && (
        <div className="px-3 pb-3 text-xs text-gray-600">
          <div className="pt-2 border-t border-gray-200">
            {section.columns.map((column, idx) => (
              <div key={column.id} className="py-1">
                Column {idx + 1}: {column.elements.length} element{column.elements.length !== 1 ? 's' : ''}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SectionItem;

